/**
 * Groups a flat item list into ordered sections by a key selector. Items
 * missing the key are bucketed into a trailing group (label/description
 * supplied by `labelSelector` when it is called with `key === null`).
 *
 * Ports the `groupBy()`/`renderGroupedSections()` algorithm from the
 * approved prototype (tickets/RHC10-15411/avid-homebook-app/prototypes/RHC10-15411.html,
 * ~L557-597) — not its markup. Used to group Home Selections items by
 * `optionCategory` and Home Colors items by `colorGroupCode`.
 */

export interface InventoryItemGroup<T> {
  /** Stable React key for this section — the raw key value, or '__none__'. */
  key: string;
  label: string;
  description?: string;
  items: T[];
}

export function groupInventoryItems<T>(
  items: T[],
  keySelector: (item: T) => string | undefined | null,
  labelSelector: (
    key: string | null,
    itemsInGroup: T[]
  ) => { label: string; description?: string }
): InventoryItemGroup<T>[] {
  const map = new Map<string | null, T[]>();

  items.forEach((item) => {
    const rawKey = keySelector(item);
    const key =
      rawKey === undefined || rawKey === null || rawKey === '' ? null : rawKey;
    const existing = map.get(key);
    if (existing) {
      existing.push(item);
    } else {
      map.set(key, [item]);
    }
  });

  // Sort by key value; the missing-key bucket (`null`) always sorts last.
  const keys = Array.from(map.keys()).sort((a, b) => {
    if (a === null) return 1;
    if (b === null) return -1;
    return a.localeCompare(b);
  });

  return keys.map((key) => {
    const groupItems = map.get(key) as T[];
    const { label, description } = labelSelector(key, groupItems);
    return {
      // `key` namespaces present values away from the `__none__` sentinel
      // reserved for the missing-key bucket, so a real key whose value is
      // literally the string "__none__" can't collide with it.
      key: key === null ? '__none__' : `key:${key}`,
      label,
      description,
      items: groupItems
    };
  });
}
